import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { PurchaseSuccessService } from '../../../core/services/purchase-success.service';
import { toImageSrc } from '../../utils/image.util';

interface PopperPiece {
  id: number;
  x: number;
  y: number;
  rotate: number;
  color: string;
  shape: 'dot' | 'ribbon' | 'spiral';
  delay: number;
  duration: number;
}

@Component({
  selector: 'app-purchase-success-modal',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './purchase-success-modal.component.html',
  styleUrl: './purchase-success-modal.component.css'
})
export class PurchaseSuccessModalComponent {
  private readonly purchaseSuccess = inject(PurchaseSuccessService);
  private readonly router = inject(Router);

  readonly data = this.purchaseSuccess.data;

  readonly heroItem = computed(() => this.data()?.items[0] ?? null);

  readonly heroCover = computed(() => {
    const url = this.heroItem()?.imageUrl;
    return url ? toImageSrc(url) : null;
  });

  readonly heroInitials = computed(() => {
    const title = this.heroItem()?.title || '?';
    return title.slice(0, 2).toUpperCase();
  });

  /** Continuous popper / confetti pieces */
  readonly popperPieces: PopperPiece[] = [
    { id: 0, x: -38, y: -52, rotate: -20, color: '#800000', shape: 'dot', delay: 0, duration: 2.2 },
    { id: 1, x: 42, y: -58, rotate: 25, color: '#C8A45C', shape: 'dot', delay: 0.3, duration: 2.5 },
    { id: 2, x: -58, y: -28, rotate: -45, color: '#A62C2C', shape: 'ribbon', delay: 0.15, duration: 2.8 },
    { id: 3, x: 62, y: -32, rotate: 40, color: '#F59E0B', shape: 'ribbon', delay: 0.45, duration: 2.4 },
    { id: 4, x: -22, y: -68, rotate: 15, color: '#800000', shape: 'dot', delay: 0.6, duration: 2.6 },
    { id: 5, x: 18, y: -72, rotate: -12, color: '#C8A45C', shape: 'dot', delay: 0.9, duration: 2.3 },
    { id: 6, x: -72, y: -12, rotate: 55, color: '#E65100', shape: 'ribbon', delay: 0.2, duration: 2.7 },
    { id: 7, x: 78, y: -8, rotate: -50, color: '#800000', shape: 'ribbon', delay: 0.55, duration: 2.9 },
    { id: 8, x: -48, y: -48, rotate: -8, color: '#A62C2C', shape: 'spiral', delay: 0.75, duration: 3.0 },
    { id: 9, x: 52, y: -46, rotate: 18, color: '#800000', shape: 'spiral', delay: 1.0, duration: 2.5 },
    { id: 10, x: -8, y: -78, rotate: 30, color: '#C8A45C', shape: 'dot', delay: 1.2, duration: 2.4 },
    { id: 11, x: 28, y: -64, rotate: -28, color: '#F59E0B', shape: 'dot', delay: 0.4, duration: 2.6 },
    { id: 12, x: -32, y: -38, rotate: -35, color: '#800000', shape: 'dot', delay: 1.4, duration: 2.8 },
    { id: 13, x: 36, y: -42, rotate: 32, color: '#A62C2C', shape: 'dot', delay: 0.8, duration: 2.2 },
    { id: 14, x: -64, y: -52, rotate: 12, color: '#C8A45C', shape: 'ribbon', delay: 1.1, duration: 2.5 },
    { id: 15, x: 68, y: -48, rotate: -15, color: '#800000', shape: 'ribbon', delay: 0.65, duration: 2.7 }
  ];

  imageSrc(value?: string | null): string | null {
    return toImageSrc(value);
  }

  formatPrice(value: number): string {
    return `₹${Number(value || 0).toFixed(2)}`;
  }

  close(): void {
    this.purchaseSuccess.close();
  }

  goLibrary(): void {
    this.purchaseSuccess.close();
    void this.router.navigate(['/books']);
  }

  continueExploring(): void {
    this.purchaseSuccess.close();
    void this.router.navigate(['/books']);
  }
}
